import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, BarController, LineController, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, BarController, LineController, Title, Tooltip, Legend);
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import DefaultModal from './components/modals/DefaultModal';
import LoadingModal from './components/modals/LoadingModal';
import SaldoProgramaModal from './components/modals/SaldoProgramaModal';
import { formatCurrency } from './utils/formatMoeda';
import { useNavigate } from 'react-router-dom';

/* ─── estilos ─── */
const STYLES = `
  @keyframes dashFadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
  @keyframes dashPulse { 0%,100%{ transform:scale(1); } 50%{ transform:scale(1.05); } }
  .dash-slide { animation: dashFadeUp .4s ease both; }
  .dash-badge { animation: dashPulse 2.4s ease-in-out infinite; }
  .dash-progress { transition: width .8s cubic-bezier(.4,0,.2,1); }
  .dash-arrow { transition: background .2s, transform .2s; }
  .dash-arrow:hover:not(:disabled) { transform: scale(1.1); }
  .dash-dot { transition: background .2s, transform .2s; }
  .dash-quick { transition: transform .2s, box-shadow .2s; }
  .dash-quick:hover { transform: translateX(3px); box-shadow: 0 4px 16px rgba(0,0,0,.12) !important; }
  @media (min-width: 992px) {
    .dash-quick-wrap { width: 220px !important; flex-shrink: 0 !important; }
  }
  @media (max-width: 991.98px) {
    .dash-container { overflow: visible !important; min-height: auto !important; height: auto !important; }
    .dash-row { flex-direction: column !important; }
    .dash-prog-card { min-height: auto !important; }
    .dash-prog-card .card { height: auto !important; }
    .dash-prog-card .card-body { overflow: visible !important; }
    .dash-rs { overflow-y: auto !important; }
  }
`;
if (!document.getElementById('dash-styles')) {
  const s = document.createElement('style');
  s.id = 'dash-styles';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

/* ─── Barra de execução ─── */
function ExecBar({ pct }) {
  const safe = Math.min(100, Math.max(0, pct));
  return (
    <div className="w-100" style={{ height: 16, borderRadius: 8, background: 'rgba(0,0,0,.07)', overflow: 'hidden' }}>
      <div className="dash-progress rounded-pill" style={{ width: `${safe}%`, height: '100%', background: pct > 100 ? '#dc3545' : '#198754' }}></div>
    </div>
  );
}

/* ─── Acesso rápido ─── */
const QUICK_LINKS = [
  { label: 'Receitas', icon: 'bi-cash-stack', color: '#198754', bg: 'rgba(25,135,84,.12)', route: '/receitas' },
  { label: 'Despesas', icon: 'bi-cash-coin', color: '#dc3545', bg: 'rgba(220,53,69,.12)', route: '/despesas' },
  { label: 'Comparativo SRES x FNDE', icon: 'bi-bar-chart-line', color: '#0dcaf0', bg: 'rgba(13,202,240,.12)', route: '/comparativo-sres-saldo-fnde' },
  { label: 'Saldo FNDE', icon: 'bi-bank', color: '#0d6efd', bg: 'rgba(13,110,253,.12)', route: '/saldo-fnde-mensal' },
  { label: 'Lista de Compras', icon: 'bi-cart3', color: '#20c997', bg: 'rgba(32,201,151,.12)', route: '/lista-compras' },
];

function QuickLink({ item, navigate }) {
  return (
    <div
      className="card border-0 dash-quick"
      onClick={() => navigate(item.route)}
      style={{ borderRadius: 12, cursor: 'pointer', background: 'rgba(255,255,255,.82)', boxShadow: '0 2px 12px rgba(0,0,0,.08)' }}
    >
      <div className="card-body py-2 px-3 d-flex align-items-center gap-2">
        <div style={{ width: 34, height: 34, borderRadius: 10, background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`bi ${item.icon}`} style={{ fontSize: '.9rem', color: item.color }}></i>
        </div>
        <span className="fw-semibold" style={{ fontSize: '.78rem', color: '#333' }}>{item.label}</span>
        <i className="bi bi-chevron-right ms-auto text-muted" style={{ fontSize: '.7rem' }}></i>
      </div>
    </div>
  );
}

/* ─── Componente principal ─── */
function Dashboard() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({});
  const [programas, setProgramas] = useState([]);
  const [page, setPage] = useState(0);
  const [showSaldoContas, setShowSaldoContas] = useState(false);
  const [receitas, setReceitas] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [saldoMensal, setSaldoMensal] = useState([]);
  const [rendimentos, setRendimentos] = useState([]);
  const [srea, setSrea] = useState([]);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const navigate = useNavigate();

  useEffect(() => {
    if (!cnpj || !ano) { setIsLoading(false); return; }
    setPage(0);
    carregarDados();
  }, [token, cnpj, ano]);

  const carregarDados = async () => {
    setIsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = { idEscola: cnpj, ano };

      const [progRes, calcRes, recRes, despRes, saldoRes, rendRes, sreaRes] = await Promise.all([
        axios.get(`${API_BASE_URL.PROGRAMA}/all`, { headers }),
        axios.get(`${API_BASE_URL.SRES}/calcular`, { params, headers }),
        axios.get(`${API_BASE_URL.RECEITA}/all`, { params, headers }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL.DESPESA}/all`, { params, headers }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL.SALDO_FNDE_MENSAL}`, { params: { idEscola: cnpj, ano }, headers }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL.RENDIMENTO}/all`, { params: { idEscola: cnpj, ano }, headers }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL.SREA}/search`, { params: { ano, idEscola: cnpj }, headers }).catch(() => ({ data: [] })),
      ]);

      setProgramas(progRes.data || []);
      setReceitas(Array.isArray(recRes.data) ? recRes.data : []);
      setDespesas(Array.isArray(despRes.data) ? despRes.data : []);
      setSaldoMensal(Array.isArray(saldoRes.data) ? saldoRes.data : []);
      setRendimentos(Array.isArray(rendRes.data) ? rendRes.data : []);
      setSrea(Array.isArray(sreaRes.data) ? sreaRes.data : []);

      const compiled = {};
      (calcRes.data || []).forEach(item => {
        const progId = item.idPrograma;
        if (!compiled[progId]) compiled[progId] = { receita: { C: 0, K: 0 }, despesa: { C: 0, K: 0 } };
        compiled[progId].receita.C += item.receitaC;
        compiled[progId].receita.K += item.receitaK;
        compiled[progId].despesa.C += item.despesaC;
        compiled[progId].despesa.K += item.despesaK;
      });
      setDashboardData(compiled);
    } catch (error) {
      showDefaultModal('error', 'Erro ao carregar dashboard: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getPrograma = (progId) => programas.find(p => p.id === progId) || { descricao: `Programa ${progId}` };
  const entries = Object.entries(dashboardData);
  const total = entries.length;
  const current = entries[page] || [];
  const programa = getPrograma(parseInt(current[0]));
  const data = current[1] || { receita: { C: 0, K: 0 }, despesa: { C: 0, K: 0 } };
  const rec = data.receita.C + data.receita.K;
  const des = data.despesa.C + data.despesa.K;
  const saldo = rec - des;
  const pct = rec > 0 ? (des / rec) * 100 : 0;
  const pctC = data.receita.C > 0 ? (data.despesa.C / data.receita.C) * 100 : 0;
  const pctK = data.receita.K > 0 ? (data.despesa.K / data.receita.K) * 100 : 0;
  const isPos = saldo >= 0;

  const anoAtual = new Date().getFullYear();
  const mesAtual = new Date().getMonth() + 1;
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const limiteMeses = Number(ano) === anoAtual ? mesAtual : 12;
  const mesesExibir = meses.slice(0, limiteMeses);
  const receitaPorMes = Array(12).fill(0);
  const despesaPorMes = Array(12).fill(0);
  const saldoPorMes = Array(12).fill(null);

  (receitas || []).filter(r => String(r.idPrograma) === String(current[0])).forEach(r => {
    const d = r.data || r.docData;
    if (!d) return;
    let m = -1;
    if (Array.isArray(d)) { m = (d[1] || 1) - 1; }
    else if (typeof d === 'string') { const dt = new Date(d); if (!isNaN(dt)) m = dt.getMonth(); }
    else if (d && typeof d === 'object') { if (d.month != null) m = (d.month - 1); else if (d.getMonth) m = d.getMonth(); }
    if (m < 0 || m > 11) return;
    const v = (parseFloat(r.vlrC || 0) + parseFloat(r.vlrK || 0)) || parseFloat(r.vlr || 0) || 0;
    receitaPorMes[m] += v;
  });

  (despesas || []).filter(d => String(d.idPrograma) === String(current[0])).forEach(d => {
    const dtStr = d.docData || d.data;
    if (!dtStr) return;
    let m = -1;
    if (Array.isArray(dtStr)) { m = (dtStr[1] || 1) - 1; }
    else if (typeof dtStr === 'string') { const dt = new Date(dtStr); if (!isNaN(dt)) m = dt.getMonth(); }
    else if (dtStr && typeof dtStr === 'object') { if (dtStr.month != null) m = dtStr.month - 1; else if (dtStr.getMonth) m = dtStr.getMonth(); }
    if (m < 0 || m > 11) return;
    despesaPorMes[m] += parseFloat(d.vlr || 0) || 0;
  });

  (saldoMensal || []).filter(s => String(s.idPrograma) === String(current[0])).forEach(s => {
    const m = (s.mes || 1) - 1;
    if (m < 0 || m > 11) return;
    const total = Number(s.cdb || 0) + Number(s.poupanca || 0) + Number(s.fundos || 0) + Number(s.conta || 0);
    if (saldoPorMes[m] == null) saldoPorMes[m] = 0;
    saldoPorMes[m] += total;
  });

  // Receita do sistema = rendimentos + receita + SREA (mensalizado)
  const receitaSistemaPorMes = Array(12).fill(0);
  // rendimentos (colunas mensais)
  (rendimentos || []).filter(r => String(r.idPrograma) === String(current[0])).forEach(r => {
    const mesesCols = ['janeiro','fevereiro','marco','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
    mesesCols.forEach((col, idx) => {
      const v = r[col];
      if (v != null) receitaSistemaPorMes[idx] += parseFloat(v) || 0;
    });
  });
  // receitas (por data)
  (receitas || []).filter(r => String(r.idPrograma) === String(current[0])).forEach(r => {
    const d = r.data || r.docData;
    if (!d) return;
    let m = -1;
    if (Array.isArray(d)) { m = (d[1] || 1) - 1; }
    else if (typeof d === 'string') { const dt = new Date(d); if (!isNaN(dt)) m = dt.getMonth(); }
    else if (d && typeof d === 'object') { if (d.month != null) m = d.month - 1; else if (d.getMonth) m = d.getMonth(); }
    if (m < 0 || m > 11) return;
    const v = (parseFloat(r.vlrC || 0) + parseFloat(r.vlrK || 0)) || parseFloat(r.vlr || 0) || 0;
    receitaSistemaPorMes[m] += v;
  });
  // SREA (anual) distribuído igualmente pelos 12 meses
  const sreaTotal = (srea || [])
    .filter(s => String(s.idPrograma) === String(current[0]))
    .reduce((acc, s) => acc + (parseFloat(s.vlrC || 0) + parseFloat(s.vlrK || 0)), 0);
  // SREA é do mês de janeiro
  receitaSistemaPorMes[0] += sreaTotal;

  // Saldo acumulado mês a mês (janeiro inclui SREA)
  const saldoSistemaPorMes = [];
  let acumulado = 0;
  for (let i = 0; i < 12; i++) {
    acumulado += (receitaSistemaPorMes[i] || 0) - (despesaPorMes[i] || 0);
    saldoSistemaPorMes.push(acumulado);
  }

  const chartData = {
    labels: mesesExibir,
    datasets: [
      { type: 'bar', label: 'Receitas', data: receitaPorMes.slice(0, limiteMeses), borderColor: '#198754', backgroundColor: 'rgba(25,135,84,0.6)', borderWidth: 1 },
      { type: 'bar', label: 'Despesas', data: despesaPorMes.slice(0, limiteMeses), borderColor: '#dc3545', backgroundColor: 'rgba(220,53,69,0.6)', borderWidth: 1 },
      { label: 'Saldo Sistema', data: saldoSistemaPorMes.slice(0, limiteMeses), borderColor: '#0d6efd', backgroundColor: 'rgba(13,110,253,0.2)', tension: 0.3, fill: false },
      { label: 'Saldo FNDE', data: saldoPorMes.slice(0, limiteMeses), borderColor: '#6f42c1', backgroundColor: 'rgba(111,66,193,0.2)', tension: 0.3, fill: false },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          generateLabels: (chart) => {
            const labels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            return labels.map((lbl) => {
              const ds = chart.data.datasets[lbl.datasetIndex];
              if (ds && String(ds.label || '').startsWith('Saldo')) {
                lbl.fillStyle = ds.borderColor || lbl.fillStyle;
                lbl.lineWidth = 1;
              }
              return lbl;
            });
          },
        },
      },
      title: { display: true, text: 'Evolução: Receitas x Despesas x Saldo FNDE' },
    },
  };

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <DefaultModal {...setDefaultModal} />
      <SaldoProgramaModal
        show={showSaldoContas}
        onClose={() => setShowSaldoContas(false)}
        idPrograma={current[0]}
        programaDescricao={programa.descricao}
      />
      <ResponsiveShadow className="dash-rs">
        <div className="dash-container px-3 py-3 d-flex flex-column gap-3" style={{ minHeight: 0, flex: 1, overflow: 'hidden' }}>

          {total > 0 ? (
            <div className="dash-row d-flex flex-column flex-lg-row gap-3" style={{ flex: 1, minHeight: 0 }}>

              {/* ═══════ CARD PROGRAMA ═══════ */}
              <div className="dash-prog-card flex-grow-1" style={{ minWidth: 0, minHeight: 0 }}>
                <div className="card border-0 h-100" style={{ borderRadius: 10, background: 'rgba(255,255,255,.85)', backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(0,0,0,.13)', overflow: 'hidden' }}>

                  {/* Header programa */}
                  <div className="px-3 px-md-4 py-2 d-flex align-items-center gap-3" style={{ borderBottom: '1px solid rgba(0,0,0,.06)', minHeight: 44 }}>
                    <div className="dash-badge" style={{ width: 38, height: 38, borderRadius: 12, background: 'linear-gradient(135deg, rgba(25,135,84,.85), rgba(32,201,151,.85))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="bi bi-diagram-3 text-white" style={{ fontSize: '1rem' }}></i>
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      
                      <div className="fw-bold text-truncate" style={{ fontSize: '.95rem' }}>{programa.descricao}</div>
                    </div>
                    <span className="text-muted flex-shrink-0" style={{ fontSize: '.75rem' }}>{page + 1}/{total}</span>
                  </div>

                  {/* Body */}
                  <div className="card-body px-3 px-md-4 py-2 d-flex flex-column gap-2" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    {/* Métricas */}
                    <div className="row g-2 g-md-3" style={{ flexShrink: 0 }}>
                      <div className="col-12 col-md-4">
                        <div className="rounded-3 h-100" style={{ background: 'rgba(25,135,84,.06)', border: '1px solid rgba(25,135,84,.15)', minHeight: 0 }}>
                          <div className="d-flex align-items-center gap-2 px-3 pt-2 pb-1" style={{ borderBottom: '1px solid rgba(25,135,84,.12)' }}>
                            <i className="bi bi-arrow-down-circle-fill text-success" style={{ fontSize: '.8rem' }}></i>
                            <span className="fw-bold" style={{ fontSize: '.68rem', color: '#198754', textTransform: 'uppercase' }}>Receita</span>
                          </div>
                          <div className="px-3 py-2">
                          <div className="d-flex justify-content-between mb-1" style={{ fontSize: '.8rem' }}>
                            <span className="text-muted">Custeio</span><span className="fw-semibold">{formatCurrency(data.receita.C)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-1" style={{ fontSize: '.8rem' }}>
                            <span className="text-muted">Capital</span><span className="fw-semibold">{formatCurrency(data.receita.K)}</span>
                          </div>
                          <div className="d-flex justify-content-between pt-2 border-top" style={{ fontSize: '.8rem' }}>
                            <span className="fw-bold">Total</span><span className="fw-bold text-success">{formatCurrency(rec)}</span>
                          </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-4">
                        <div className="rounded-3 h-100" style={{ background: 'rgba(220,53,69,.06)', border: '1px solid rgba(220,53,69,.15)', minHeight: 0 }}>
                          <div className="d-flex align-items-center gap-2 px-3 pt-2 pb-1" style={{ borderBottom: '1px solid rgba(220,53,69,.12)' }}>
                            <i className="bi bi-arrow-up-circle-fill text-danger" style={{ fontSize: '.8rem' }}></i>
                            <span className="fw-bold" style={{ fontSize: '.68rem', color: '#dc3545', textTransform: 'uppercase' }}>Despesa</span>
                          </div>
                          <div className="px-3 py-2">
                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '.8rem' }}>
                              <span className="text-muted">Custeio</span><span className="fw-semibold">{formatCurrency(data.despesa.C)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '.8rem' }}>
                              <span className="text-muted">Capital</span><span className="fw-semibold">{formatCurrency(data.despesa.K)}</span>
                            </div>
                            <div className="d-flex justify-content-between pt-2 border-top" style={{ fontSize: '.8rem' }}>
                              <span className="fw-bold">Total</span><span className="fw-bold text-danger">{formatCurrency(des)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-4">
                        <div
                          className="rounded-3 h-100"
                          style={{ background: isPos ? 'rgba(13,110,253,.06)' : 'rgba(220,53,69,.06)', border: `1px solid ${isPos ? 'rgba(13,110,253,.15)' : 'rgba(220,53,69,.15)'}`, minHeight: 0, cursor: 'pointer' }}
                          onClick={() => setShowSaldoContas(true)}
                          title="Clique para ver o saldo por contas"
                        >
                          <div className="d-flex align-items-center gap-2 px-3 pt-2 pb-1" style={{ borderBottom: `1px solid ${isPos ? 'rgba(13,110,253,.12)' : 'rgba(220,53,69,.12)'}` }}>
                            <i className={`bi ${isPos ? 'bi-piggy-bank text-primary' : 'bi-exclamation-triangle-fill text-danger'}`} style={{ fontSize: '.8rem' }}></i>
                            <span className="fw-bold" style={{ fontSize: '.68rem', color: isPos ? '#0d6efd' : '#dc3545', textTransform: 'uppercase' }}>Saldo</span>
                            <i className="bi bi-box-arrow-up-right ms-auto text-muted" style={{ fontSize: '.68rem' }}></i>
                          </div>
                          <div className="px-3 py-2">
                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '.8rem' }}>
                              <span className="text-muted">Custeio</span>
                              <span className={`fw-semibold ${(data.receita.C - data.despesa.C) >= 0 ? 'text-primary' : 'text-danger'}`}>
                                {formatCurrency(data.receita.C - data.despesa.C)}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '.8rem' }}>
                              <span className="text-muted">Capital</span>
                              <span className={`fw-semibold ${(data.receita.K - data.despesa.K) >= 0 ? 'text-primary' : 'text-danger'}`}>
                                {formatCurrency(data.receita.K - data.despesa.K)}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between pt-2 border-top" style={{ fontSize: '.8rem' }}>
                              <span className="fw-bold">Total</span>
                              <span className={`fw-bold ${isPos ? 'text-primary' : 'text-danger'}`}>
                                <i className={`bi ${isPos ? 'bi-arrow-up-circle-fill' : 'bi-arrow-down-circle-fill'} me-1`} style={{ fontSize: '.8rem' }}></i>
                                {formatCurrency(saldo)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex flex-column flex-md-row gap-3 align-items-stretch" style={{ width: '100%', flex: 1, minHeight: 0 }}>
                      {/* Execução (30%) */}
                      <div className="rounded-3 p-3 d-flex flex-column justify-content-between" style={{ background: 'rgba(0,0,0,.02)', flex: '0 0 30%', minWidth: 0, minHeight: 0 }}>
                        <div className="mb-1" style={{ fontSize: '.78rem' }}>
                          <span className="fw-semibold text-muted">Execução Orçamentária</span>
                        </div>
                        <div className="mb-1">
                          <div className="d-flex justify-content-between mb-1" style={{ fontSize: '.72rem' }}>
                            <span className="text-muted">Custeio</span>
                            <span className={`fw-bold ${pctC > 100 ? 'text-danger' : 'text-success'}`}>{pctC.toFixed(1)}%</span>
                          </div>
                          <ExecBar pct={pctC} />
                        </div>
                        <div className="mb-1">
                          <div className="d-flex justify-content-between mb-1" style={{ fontSize: '.72rem' }}>
                            <span className="text-muted">Capital</span>
                            <span className={`fw-bold ${pctK > 100 ? 'text-danger' : 'text-success'}`}>{pctK.toFixed(1)}%</span>
                          </div>
                          <ExecBar pct={pctK} />
                        </div>
                        <div>
                          <div className="d-flex justify-content-between mb-1" style={{ fontSize: '.72rem' }}>
                            <span className="fw-semibold text-muted">Total</span>
                            <span className={`fw-bold ${pct > 100 ? 'text-danger' : 'text-success'}`}>{pct.toFixed(1)}%</span>
                          </div>
                          <ExecBar pct={pct} />
                        </div>
                      </div>

                      {/* Gráfico (70%) */}
                      <div className="rounded-3 p-2 d-flex flex-column" style={{ background: 'rgba(0,0,0,.02)', flex: '1 1 0', minWidth: 0, minHeight: 0 }}>
                        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                          <Line data={chartData} options={chartOptions} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer com navegação */}
                  <div className="px-3 px-md-4 py-2 d-flex align-items-center justify-content-center gap-3" style={{ borderTop: '1px solid rgba(0,0,0,.06)', flexShrink: 0 }}>
                    <button
                      className="btn btn-outline-secondary dash-arrow"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      style={{ borderRadius: '50%', width: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <i className="bi bi-chevron-left" style={{ fontSize: '.85rem' }}></i>
                    </button>
                    <div className="d-flex align-items-center gap-1">
                      {entries.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setPage(i)}
                          className="dash-dot"
                          style={{
                            width: i === page ? 20 : 8, height: 8, borderRadius: 4, padding: 0, border: 'none',
                            background: i === page ? '#198754' : '#ced4da',
                          }}
                        />
                      ))}
                    </div>
                    <button
                      className="btn btn-outline-secondary dash-arrow"
                      onClick={() => setPage(p => Math.min(total - 1, p + 1))}
                      disabled={page >= total - 1}
                      style={{ borderRadius: '50%', width: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <i className="bi bi-chevron-right" style={{ fontSize: '.85rem' }}></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* ═══════ CARD ACESSO RÁPIDO ═══════ */}
              <div className="dash-quick-wrap d-flex flex-column mt-auto mt-lg-0" style={{ width: '100%' }}>
                <div className="card border-0 h-100" style={{ borderRadius: 10, background: 'rgba(255,255,255,.85)', backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(0,0,0,.13)', overflow: 'hidden' }}>
                  {/* Header */}
                  <div className="px-3 px-md-4 py-2 d-flex align-items-center gap-3" style={{ borderBottom: '1px solid rgba(0,0,0,.06)', minHeight: 44 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 12, background: 'rgba(255,193,7,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="bi bi-lightning-charge" style={{ fontSize: '.9rem', color: '#e67700' }}></i>
                    </div>
                    <span className="fw-bold" style={{ fontSize: '.82rem' }}>Acesso Rápido</span>
                  </div>
                  {/* Body com scroll */}
                  <div className="card-body p-2 d-flex flex-column gap-2" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
                    {QUICK_LINKS.map((item) => (
                      <QuickLink key={item.route} item={item} navigate={navigate} />
                    ))}
                  </div>
                </div>
              </div>

            </div>
          ) : (
            !isLoading && (
              <div className="card border-0 text-center py-5" style={{ borderRadius: 18, background: 'rgba(255,255,255,.85)', boxShadow: '0 4px 20px rgba(0,0,0,.08)' }}>
                <div className="card-body">
                  <i className="bi bi-inbox text-muted" style={{ fontSize: '2.5rem' }}></i>
                  <p className="text-muted mt-2 mb-0">Nenhum dado disponível para o ano/escola selecionada.</p>
                </div>
              </div>
            )
          )}
        </div>
      </ResponsiveShadow>
    </Layout>
  );
}

export default Dashboard;
